import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

export default function InvoiceDetailModal({ invoice, onClose }) {
  if (!invoice) return null;

  const formatRupiah = (num) => Number(num || 0).toLocaleString("id-ID");

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  const InfoRow = ({ label, value }) => (
    <div className="flex justify-between py-2 border-b border-slate-200 dark:border-slate-800 text-sm">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="font-medium text-slate-800 dark:text-slate-200">
        {value}
      </span>
    </div>
  );

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white dark:bg-slate-900 w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
        >
          {/* ================= HEADER ================= */}
          <div className="sticky top-0 bg-white dark:bg-slate-900 p-4 border-b dark:border-slate-800 flex justify-between items-center z-20">
            <div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                Invoice Detail
              </h2>
              <p className="text-xs text-slate-500">#{invoice.invoicenum}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
            >
              <X className="w-5 h-5 dark:text-slate-400" />
            </button>
          </div>

          {/* ================= CONTENT ================= */}
          <div className="p-6 space-y-6">
            {/* CUSTOMER */}
            <div className="bg-slate-50 dark:bg-slate-800/20 p-4 rounded-xl border dark:border-slate-800">
              <p className="text-xs uppercase font-bold text-slate-400 mb-1">
                Customer
              </p>
              <p className="font-semibold text-slate-800 dark:text-white">
                {invoice.firstname} {invoice.lastname}
              </p>
              <p className="text-xs text-slate-500">
                Customer ID: {invoice.cust_id}
              </p>
            </div>

            {/* INFO */}
            <div>
              <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-3">
                <div className="w-1.5 h-5 bg-blue-600 rounded-full"></div>
                Informasi Invoice
              </h3>
              <div className="bg-white dark:bg-slate-800/40 border dark:border-slate-800 rounded-xl p-4">
                <InfoRow
                  label="Invoice Date"
                  value={formatDate(invoice.date)}
                />
                <InfoRow label="Due Date" value={formatDate(invoice.duedate)} />
                <InfoRow
                  label="Date Paid"
                  value={formatDate(invoice.datepaid)}
                />
                <InfoRow
                  label="Payment Method"
                  value={invoice.paymentmethod || "-"}
                />
                <InfoRow label="Status" value={invoice.status} />
              </div>
            </div>

            {/* AMOUNT */}
            <div>
              <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-3">
                <div className="w-1.5 h-5 bg-green-600 rounded-full"></div>
                Ringkasan Pembayaran
              </h3>
              <div className="space-y-2 bg-blue-50/50 dark:bg-blue-900/10 p-5 rounded-2xl border border-blue-100 dark:border-blue-900/30 text-sm">
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Subtotal</span>
                  <span className="font-mono">
                    Rp {formatRupiah(invoice.subtotal)}
                  </span>
                </div>
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Tax</span>
                  <span className="font-mono">
                    Rp {formatRupiah(invoice.tax)}
                  </span>
                </div>
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Tax 2</span>
                  <span className="font-mono">
                    Rp {formatRupiah(invoice.tax2)}
                  </span>
                </div>
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Credit</span>
                  <span className="font-mono">
                    Rp {formatRupiah(invoice.credit)}
                  </span>
                </div>

                <div className="pt-4 flex justify-between items-center">
                  <span className="font-bold text-slate-800 dark:text-white">
                    Total Tagihan
                  </span>
                  <span className="text-2xl font-black text-blue-600 dark:text-blue-400 font-mono">
                    Rp {formatRupiah(invoice.total)}
                  </span>
                </div>
              </div>
            </div>

            {/* NOTES */}
            {invoice.notes && (
              <div>
                <label className="block text-sm font-semibold mb-2 dark:text-slate-300">
                  Catatan
                </label>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 border dark:border-slate-700 rounded-xl text-sm dark:text-slate-300">
                  {invoice.notes}
                </div>
              </div>
            )}
          </div>

          {/* ================= FOOTER ================= */}
          <div className="flex justify-end gap-3 p-4 border-t dark:border-slate-800">
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all font-medium"
            >
              Tutup
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
